import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setProgress } from "../../redux/slice/system";

import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  Tooltip,
  Legend,
  Scatter,
  ResponsiveContainer,
} from "recharts";

import { ModuleBlockTitle } from "../diagram/components";
import { decimalAddZero } from "../../service/common";
import { option_colData } from "../../service/option/common";

import { classNames } from "primereact/utils";

const useChartBar = ({
  setting = {},
  isExport = true,
  isExportRawList = false,
  isFilter = false,
} = {}) => {
  const dispatch = useDispatch();

  const renderBar = useCallback(
    ({
      className = "",
      moduleTitle = "",
      targetId = "chart_bar",
      options,
    } = {}) => {
      return (
        <>
          <ModuleBlockTitle
            type="chart_bar"
            moduleTitle={moduleTitle}
            isExport={isExport}
            isExportRawList={isExportRawList}
            isFilter={isFilter}
            options={{
              setting,
              targetId,
              exportColumns: options.moduleData.exportColumns,
              tbData: options.chartData,
              callMainAPI: options.callMainAPI,
              progressAction: (flag) => dispatch(setProgress({ show: flag })),
            }}
          />
          <div
            className={classNames(
              "border-1 border-top-none border-400",
              className
            )}
          >
            <div id={targetId} className="h-full bg-white p-3">
              <ModuleBar {...options} />
            </div>
          </div>
        </>
      );
    },
    []
  );

  return {
    renderBar,
  };
};

const ModuleBar = ({
  moduleData = {},
  chartData = [],
  chartMargin = {
    top: 30,
    right: 20,
    bottom: 20,
    left: 20,
  },
  clickEvent = () => {},
}) => {
  const [showFilter, setShowFilter] = useState(null);
  const [finalData, setFinalData] = useState(null);

  useEffect(() => {
    let temp = [
      ...(moduleData.barColumns || []),
      ...(moduleData.lineColumns || []),
    ].reduce((pre, cur) => ({ ...pre, [cur.key]: true }), {});
    setShowFilter(temp);
  }, [chartData]);

  useEffect(() => {
    if (showFilter === null) return;

    // let tempData = [...chartData.format];

    // if (moduleData.isLeftBarFull || moduleData.isRightBarFull) {
    //   tempData = tempData.map((item) => {
    //     let total = moduleData.barColumns.reduce(
    //       (pre, cur) => (pre += item[cur.key]),
    //       0
    //     );

    //     moduleData.barColumns.forEach((column) => {
    //       item[`${column.key}_percent`] =
    //         Math.round((item[column.key] / total) * 1000) / 10;
    //     });

    //     return item;
    //   });
    // }
    setFinalData(chartData.format);
  }, [chartData]);

  const getAxisYDomain = (ref) => {
    if (!finalData || finalData.length == 0) return [0, 10];

    let [bottom, top] = [
      moduleData[`${ref}Columns`].reduce(
        (pre, cur) => (pre += finalData[0][cur.key]),
        0
      ),
      moduleData[`${ref}Columns`].reduce(
        (pre, cur) => (pre += finalData[0][cur.key]),
        0
      ),
    ];
    finalData.forEach((d) => {
      let value = moduleData[`${ref}Columns`].reduce(
        (pre, cur) => (pre += d[cur.key]),
        0
      );
      if (value > top) top = value;
      if (value < bottom) bottom = value;
    });
    return [0, (top + top * 0.1) | 0];
  };

  const CustomTooltip = ({ active, payload, label }) => {
    const columns = [
      ...(moduleData.barColumns || []),
      ...(moduleData.lineColumns || []),
    ].reduce(
      (pre, cur) => ({
        ...pre,
        [cur.key]: cur,
      }),
      {}
    );

    if (active && payload && payload.length) {
      return (
        <div className="line-height-3 bg-white border-1 border-400 p-2">
          <div className="font-bold mb-1">{label}</div>
          {payload.map((item, index) => (
            <div
              key={`tooltip_${item.dataKey}`}
              className="flex align-items-center"
            >
              <div
                className="flex-shrink-0 w-1rem h-1rem mr-1"
                style={{
                  backgroundColor:
                    columns[item.dataKey]?.color ?? `${option_colData[index]}`,
                }}
              ></div>
              <span>{columns[item.dataKey]?.label}：</span>
              <span>
                {decimalAddZero({ rawVal: item.value, decimalShowRaw: true })}
              </span>
              <span className="ml-1">
                {moduleData[`${columns[item.dataKey]?.ui}Unit`]}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderCustomizedLabel = (props) => {
    const { x, y, width, height, value } = props;

    return (
      <g>
        <text
          fontSize={12}
          fill="black"
          x={x + (width === undefined ? 0 : width / 2)}
          y={y - 8}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {value}
        </text>
      </g>
    );
  };

  const renderXYAxis = (options) => {
    if (moduleData.isVertical) {
      return <XAxis {...options} />;
    } else {
      return <YAxis {...options} />;
    }
  };

  return (
    <>
      <div className="flex flex-wrap -m-1">
        {[
          ...(moduleData.barColumns || []),
          ...(moduleData.lineColumns || []),
        ]?.map((item, index) => (
          <div
            key={`legend_${item.key}`}
            className={classNames(
              "flex align-items-center hover:text-gray-500 cursor-pointer m-1 mr-2",
              {
                "opacity-50": !showFilter?.[item.key],
              }
            )}
            onClick={() =>
              setShowFilter((state) => ({
                ...state,
                [item.key]: !state[item.key],
              }))
            }
          >
            {item.ui === "bar" && (
              <div
                className="flex-shrink-0 w-1rem h-1rem mr-1"
                style={{
                  backgroundColor: item?.color ?? `${option_colData[index]}`,
                }}
              ></div>
            )}
            {item.ui === "line" && (
              <div
                className="relative flex align-items-center justify-content-center flex-shrink-0 mr-1"
                style={{ width: "1.5rem", height: "1.5rem" }}
              >
                <div
                  className="w-full border-top-2"
                  style={{
                    borderColor: item?.color ?? `${option_colData[index]}`,
                  }}
                ></div>
                <div
                  className="absolute bg-white border-2 border-circle"
                  style={{
                    width: "0.6rem",
                    height: "0.6rem",
                    marginTop: "1px",
                    borderColor: item?.color ?? `${option_colData[index]}`,
                  }}
                ></div>
              </div>
            )}
            <div>{item.label}</div>
          </div>
        ))}
      </div>

      <div className="h-20rem mt-2">
        <ResponsiveContainer height="100%">
          <ComposedChart
            layout={moduleData.isVertical ? "vertical" : "horizontal"}
            width="100%"
            height="100%"
            data={finalData}
            margin={chartMargin}
          >
            <CartesianGrid stroke="#f5f5f5" />
            {moduleData.isVertical ? (
              <YAxis dataKey={moduleData.xKey} type="category" />
            ) : (
              <XAxis dataKey={moduleData.xKey} />
            )}

            {moduleData.barColumns?.length > 0 && (
              <>
                {renderXYAxis({
                  label: {
                    value: moduleData.barUnit,
                    angle: 0,
                    position: moduleData.isVertical
                      ? "insideRight"
                      : "insideTop",
                    dx: moduleData.isVertical ? 34 : 30,
                    dy: moduleData.isVertical ? -3 : -25,
                  },
                  orientation: moduleData.isVertical ? "bottom" : "left",
                  allowDataOverflow: true,
                  domain: moduleData.leftYDomain ?? getAxisYDomain("bar"),
                  type: "number",
                  [moduleData.isVertical ? "xAxisId" : "yAxisId"]: "bar",
                })}
              </>
            )}
            {moduleData.lineColumns?.length > 0 && !!moduleData.isYMulti && (
              <>
                {renderXYAxis({
                  label: {
                    value: moduleData.lineUnit,
                    angle: 0,
                    position: moduleData.isVertical
                      ? "insideRight"
                      : "insideTop",
                    dx: moduleData.isVertical ? 34 : -30,
                    dy: moduleData.isVertical ? -3 : -25,
                  },
                  orientation: "right",
                  allowDataOverflow: true,
                  domain: moduleData.rightYDomain ?? getAxisYDomain("line"),
                  type: "number",
                  [moduleData.isVertical ? "xAxisId" : "yAxisId"]: "line",
                })}
              </>
            )}

            <Tooltip content={<CustomTooltip />} />
            {(moduleData.barColumns || []).map((item, index) => (
              <React.Fragment key={`bar_${item.key}`}>
                {showFilter?.[item.key] && (
                  <Bar
                    {...{
                      [moduleData.isVertical ? "xAxisId" : "yAxisId"]: "bar",
                      dataKey: item.key,
                      stackId: moduleData.isBarStack ? "bar" : null,
                      barSize: 20,
                      fill: item?.color ?? `${option_colData[index]}`,
                    }}
                  >
                    <LabelList
                      dataKey={item.key}
                      content={renderCustomizedLabel}
                    />
                  </Bar>
                )}
              </React.Fragment>
            ))}
            {(moduleData.lineColumns || []).map((item, index) => (
              <React.Fragment key={`line_${item.key}`}>
                {showFilter?.[item.key] && (
                  <Line
                    strokeWidth={2}
                    {...{
                      [moduleData.isVertical ? "xAxisId" : "yAxisId"]:
                        !!moduleData.isYMulti ? "line" : "bar",
                      dataKey: item.key,
                      stroke:
                        item?.color ??
                        option_colData[index + moduleData.barColumns.length],
                    }}
                  >
                    <LabelList
                      dataKey={item.key}
                      content={renderCustomizedLabel}
                    />
                  </Line>
                )}
              </React.Fragment>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default useChartBar;
